<template>
  <b-modal
    id="bulk-hours-modal"
    modal-class="timesheet-modal"
    size="lg"
    title="Add Hours"
    :ok-title="okTitle"
    :ok-disabled="!canSubmit || submitting"
    :no-close-on-backdrop="submitting"
    :hide-header-close="submitting"
    @ok="$emit('ok', $event)"
    @hidden="$emit('hidden')"
  >
    <template #modal-ok>
      <b-spinner v-if="submitting" small class="mr-1" />
      {{ okTitle }}
    </template>

    <b-card no-body class="mb-3">
      <b-card-header
        class="py-2 font-weight-bold d-flex align-items-center justify-content-between"
      >
        <span>Workers</span>
        <button
          type="button"
          class="worker-select-all-btn"
          :disabled="!workers.length || submitting"
          @click="toggleSelectAll"
        >
          {{ allSelected ? 'Unselect All' : 'Select All' }}
        </button>
      </b-card-header>
      <b-list-group flush class="worker-list">
        <b-list-group-item
          v-for="worker in workers"
          :key="worker.tradieId"
          class="py-2"
        >
          <b-form-checkbox
            :checked="form.selected_ids.includes(worker.tradieId)"
            :disabled="submitting"
            @change="toggleWorker(worker.tradieId, $event)"
          >
            <span class="font-weight-medium worker-name">{{
              worker.tradieName
            }}</span>
            <span
              v-if="worker.trade"
              class="text-muted small ml-2 worker-trade"
            >
              {{ worker.trade }}
            </span>
          </b-form-checkbox>
        </b-list-group-item>
        <b-list-group-item v-if="!workers.length" class="py-3 text-muted">
          No workers in this project.
        </b-list-group-item>
      </b-list-group>
    </b-card>

    <b-form-group label="Type of Hours">
      <div class="entry-seg" role="group">
        <button
          v-for="opt in leaveTypeOptions"
          :key="opt.value || 'worked'"
          type="button"
          class="entry-seg-btn"
          :class="{ active: form.leave_type === opt.value }"
          :disabled="submitting"
          @click="selectLeaveType(opt.value)"
        >
          {{ opt.text }}
        </button>
      </div>
    </b-form-group>

    <div class="d-flex" style="gap: 12px">
      <b-form-group label="Start date" class="flex-fill">
        <b-form-datepicker
          v-model="form.start_date"
          :max="maxFutureDate"
          :disabled="submitting"
          boundary="window"
          locale="en-AU"
          today-button
          reset-button
          close-button
          show-decade-nav
        />
      </b-form-group>
      <b-form-group label="End date" class="flex-fill">
        <b-form-datepicker
          v-model="form.end_date"
          :min="form.start_date || undefined"
          :max="maxFutureDate"
          :disabled="submitting"
          boundary="window"
          locale="en-AU"
          today-button
          reset-button
          close-button
          show-decade-nav
        />
      </b-form-group>
    </div>

    <b-form-group label="Days included">
      <div class="entry-seg entry-seg--pair" role="group">
        <button
          type="button"
          class="entry-seg-btn"
          :class="{ active: !form.include_weekends }"
          :disabled="submitting"
          @click="form.include_weekends = false"
        >
          Weekdays
        </button>
        <button
          type="button"
          class="entry-seg-btn"
          :class="{ active: form.include_weekends }"
          :disabled="submitting"
          @click="form.include_weekends = true"
        >
          All days
        </button>
      </div>
      <p v-if="dayCount" class="entry-summary">
        <strong>{{ dayCount }} day{{ dayCount === 1 ? '' : 's' }}</strong>
        will be added for each selected worker{{
          isLeave ? '.' : ', each with the same times.'
        }}
        <template v-if="weekendDaysSkipped">
          {{ weekendDaysSkipped }} weekend day{{
            weekendDaysSkipped === 1 ? '' : 's'
          }}
          skipped.
        </template>
      </p>
    </b-form-group>

    <div v-if="!isLeave" class="d-flex" style="gap: 12px">
      <b-form-group label="Start" class="flex-fill">
        <datetime-input
          v-model="form.start_time"
          type="time"
          :disabled="submitting"
        />
      </b-form-group>
      <b-form-group label="Finish" class="flex-fill">
        <datetime-input
          v-model="form.finish_time"
          type="time"
          :disabled="submitting"
        />
      </b-form-group>
      <b-form-group label="Break (minutes)" class="flex-fill">
        <b-form-input
          v-model.number="form.break_minutes"
          type="number"
          min="0"
          :disabled="submitting"
        />
      </b-form-group>
    </div>
    <b-form-group v-else label="Hours per day">
      <b-form-input
        v-model.number="form.leave_hours"
        type="number"
        min="0"
        max="24"
        step="0.5"
        placeholder="Company default"
        :disabled="submitting"
      />
      <small class="entry-hint">
        Leave blank to use the company default for this leave type.
      </small>
    </b-form-group>

    <b-form-group label="Comment (visible to your team)">
      <b-form-textarea v-model="form.comment" rows="2" :disabled="submitting" />
    </b-form-group>
  </b-modal>
</template>

<script>
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'
import { LEAVE_TYPE_OPTIONS } from '@/utils/timesheetHelpers'

export default {
  name: 'TimesheetBulkHoursModal',
  components: { DatetimeInput },
  props: {
    form: { type: Object, required: true },
    workers: { type: Array, default: () => [] },
    today: { type: String, default: '' },
    maxFutureDate: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
  },
  computed: {
    leaveTypeOptions() {
      return LEAVE_TYPE_OPTIONS
    },
    isLeave() {
      return !!this.form.leave_type
    },
    allSelected() {
      return (
        this.workers.length > 0 &&
        this.workers.every((w) => this.form.selected_ids.includes(w.tradieId))
      )
    },
    okTitle() {
      const n = this.form.selected_ids.length
      if (!n) return 'Apply'
      return `Apply to ${n} Worker${n === 1 ? '' : 's'}`
    },
    // Inclusive walk of the range, split into what will be created and what
    // the weekend rule drops — mirrors TimesheetAddEntryModal.rangeDays.
    rangeDays() {
      const empty = { included: 0, weekendsSkipped: 0 }
      if (!this.form.start_date || !this.form.end_date) return empty
      const start = new Date(`${this.form.start_date}T00:00:00`)
      const end = new Date(`${this.form.end_date}T00:00:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return empty
      }
      if (end < start) return empty
      const spanDays = Math.round((end - start) / 86400000) + 1
      let included = 0
      let weekendsSkipped = 0
      for (let i = 0; i < spanDays; i += 1) {
        const day = new Date(start)
        day.setDate(day.getDate() + i)
        const dow = day.getDay()
        const isWeekend = dow === 0 || dow === 6
        if (this.form.include_weekends || !isWeekend) {
          included += 1
        } else {
          weekendsSkipped += 1
        }
      }
      return { included, weekendsSkipped }
    },
    dayCount() {
      return this.rangeDays.included
    },
    weekendDaysSkipped() {
      return this.rangeDays.weekendsSkipped
    },
    canSubmit() {
      if (!this.form.selected_ids.length || !this.form.project_id) return false
      if (!this.form.start_date || !this.form.end_date) return false
      if (this.form.end_date < this.form.start_date) return false
      if (!this.dayCount) return false
      if (!this.isLeave && (!this.form.start_time || !this.form.finish_time)) {
        return false
      }
      return true
    },
  },
  watch: {
    // Moving the start date past the current end date left an invalid range
    // (end < start) sitting silently in the form. Snap end forward to
    // follow start — mirrors the same fix in TimesheetAddEntryModal.vue.
    'form.start_date'(newStart) {
      if (this.form.end_date && newStart && this.form.end_date < newStart) {
        this.form.end_date = newStart
      }
    },
  },
  methods: {
    toggleWorker(tradieId, checked) {
      const ids = this.form.selected_ids.slice()
      const index = ids.indexOf(tradieId)
      if (checked && index === -1) {
        ids.push(tradieId)
      } else if (!checked && index !== -1) {
        ids.splice(index, 1)
      }
      this.form.selected_ids = ids
    },
    toggleSelectAll() {
      this.form.selected_ids = this.allSelected
        ? []
        : this.workers.map((w) => w.tradieId)
    },
    selectLeaveType(value) {
      this.form.leave_type = value
      // Clock fields and leave hours are mutually exclusive — clear the side
      // that just became unreachable, mirroring TimesheetAddEntryModal.
      if (value) {
        this.form.start_time = ''
        this.form.finish_time = ''
        this.form.break_minutes = 0
      } else {
        this.form.leave_hours = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.worker-select-all-btn {
  border: 0;
  background: none;
  padding: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #006cff;

  &:disabled {
    color: #adb5bd;
    cursor: not-allowed;
  }
}

.worker-list {
  max-height: 260px;
  overflow-y: auto;
}

.worker-name {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
}

.worker-trade {
  color: #64748b;
  font-size: 0.72rem;
}

.entry-seg {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 5px;
  overflow: hidden;
}

.entry-seg-btn {
  flex: 1;
  border: 0;
  border-right: 1px solid #ced4da;
  background: #fff;
  padding: 9px 4px;
  font-size: 12.5px;
  color: #495057;
  font-family: inherit;
  line-height: 1.2;

  &:last-child {
    border-right: 0;
  }

  &.active {
    background: #006cff;
    color: #fff;
    font-weight: 600;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.entry-seg--pair .entry-seg-btn {
  padding: 10px 6px;
  font-size: 13px;
}

.entry-summary {
  font-size: 12.5px;
  color: #6b7280;
  margin: 10px 0 0;
  line-height: 1.5;
}

.entry-hint {
  display: block;
  font-size: 11.5px;
  color: #6b7280;
  margin: 4px 0 0;
  line-height: 1.3;
}
</style>
